import React, { useState, useEffect } from "react";
import "./attendanceList.scss";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import { FaUsers, FaCalendarAlt, FaDownload } from "react-icons/fa";
import BeatLoader from "react-spinners/BeatLoader";
import { auth } from "../../firebase";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5001/api";

const AttendanceList = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [attendees, setAttendees] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const availableYears = [2024, 2025, 2026, 2027, 2028];

  const fetchAttendanceData = async (year) => {
    setLoading(true);
    try {
      // Get auth token
      const token = auth.currentUser
        ? await auth.currentUser.getIdToken(true)
        : null;
      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      // Fetch attendees for the year
      const attendeesResponse = await fetch(
        `${API_BASE_URL}/attendance/year/${year}`,
        { headers }
      );
      if (attendeesResponse.ok) {
        const attendeesData = await attendeesResponse.json();
        setAttendees(attendeesData.data || []);
      }

      // Fetch statistics for the year
      const statsResponse = await fetch(
        `${API_BASE_URL}/attendance/stats/${year}`,
        { headers }
      );
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error("Error fetching attendance data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendanceData(selectedYear);
  }, [selectedYear]);

  const handleYearChange = (e) => {
    setSelectedYear(parseInt(e.target.value));
  };

  const exportToCSV = () => {
    if (attendees.length === 0) return;

    const headers = [
      "Name",
      "Email",
      "Phone",
      "Membership Code",
      "Attendance Date",
    ];
    const csvContent = [
      headers.join(","),
      ...attendees.map((attendee) =>
        [
          `"${attendee.users?.display_name || ""}"`,
          `"${attendee.users?.email || ""}"`,
          `"${attendee.users?.phone || ""}"`,
          `"${attendee.users?.membership_code || ""}"`,
          `"${new Date(attendee.attended_date).toLocaleDateString()}"`,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `AGM_${selectedYear}_Attendees.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="attendance-list">
        <Sidebar />
        <div className="attendance-container">
          <Navbar />
          <div className="loaderContainer">
            <BeatLoader color="#7451f8" />
            <p>Loading attendance data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="attendance-list">
      <Sidebar />
      <div className="attendance-container">
        <Navbar />
        <div className="attendance-content">
          <div className="attendance-header">
            <div className="header-left">
              <FaCalendarAlt className="header-icon" />
              <h1>AGM Attendance</h1>
            </div>
            <div className="header-controls">
              <select
                value={selectedYear}
                onChange={handleYearChange}
                className="year-selector"
              >
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year} AGM
                  </option>
                ))}
              </select>
              <button
                onClick={exportToCSV}
                className="export-btn"
                disabled={attendees.length === 0}
              >
                <FaDownload /> Export CSV
              </button>
            </div>
          </div>

          {stats && (
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">
                  <FaUsers />
                </div>
                <div className="stat-content">
                  <h3>{stats.attendeesCount}</h3>
                  <p>Attendees</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <FaUsers />
                </div>
                <div className="stat-content">
                  <h3>{stats.totalUsers}</h3>
                  <p>Total Members</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <FaCalendarAlt />
                </div>
                <div className="stat-content">
                  <h3>{stats.attendanceRate}%</h3>
                  <p>Attendance Rate</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <FaUsers />
                </div>
                <div className="stat-content">
                  <h3>{stats.absentCount}</h3>
                  <p>Absent</p>
                </div>
              </div>
            </div>
          )}

          <div className="attendees-table-container">
            <div className="table-header">
              <h2>
                {selectedYear} AGM Attendees ({attendees.length})
              </h2>
            </div>

            {attendees.length > 0 ? (
              <div className="attendees-table">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Membership Code</th>
                      <th>Attendance Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendees.map((attendee) => (
                      <tr key={attendee.id}>
                        <td>{attendee.users?.display_name || "N/A"}</td>
                        <td>{attendee.users?.email || "N/A"}</td>
                        <td>{attendee.users?.phone || "N/A"}</td>
                        <td>{attendee.users?.membership_code || "N/A"}</td>
                        <td>
                          {new Date(
                            attendee.attended_date
                          ).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="no-attendees">
                <FaUsers className="no-data-icon" />
                <h3>No attendees found for {selectedYear}</h3>
                <p>
                  No one has been marked as attending the {selectedYear} AGM
                  yet.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceList;
